import { useState, useRef, useCallback, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';
import type { ConversationStatus } from '@elevenlabs/react';
import type { ConversationConfigOverrideAgentLanguage, DisconnectionDetails } from '@elevenlabs/types';
import { useTranslation } from 'react-i18next';
import type { SessionStatus, ConversationMessage } from '@/types/conversation.ts';
import type { SimulationResponse } from '@/types/simulation.ts';
import type { Maestro } from '@/types/maestro.ts';
import conversationService, { SessionInitError } from '@/services/conversationService.ts';
import type { SessionInitResponse } from '@/services/conversationService.ts';
import sessionService from '@/services/sessionService.ts';
import { useAlert } from '@/context/AlertContext.tsx';
import { MAESTRO_MOCK_RESPONSES, DEFAULT_MOCK_RESPONSES } from '@/data/maestroMockResponses.ts';

const voiceEnabled = import.meta.env.VITE_VOICE_ENABLED !== 'false';

interface UseSimulationSessionReturn {
  status: SessionStatus;
  messages: ConversationMessage[];
  elapsedSeconds: number;
  sessionId: string | null;
  /** Display name of the AI buyer persona (e.g. "Alejandro Ruiz"), or null. */
  personaName: string | null;
  connectionStatus: ConversationStatus;
  isSpeaking: boolean;
  isMuted: boolean;
  isPaused: boolean;
  isStarting: boolean;
  wasInterrupted: boolean;
  initError: string | null;
  clearInitError: () => void;
  voiceEnabled: boolean;
  startSession: (simulation: SimulationResponse, maestro: Maestro, isQuickPractice?: boolean) => Promise<void>;
  requestFinish: () => void;
  confirmFinish: () => Promise<void>;
  cancelFinish: () => void;
  goBackToList: () => void;
  toggleMute: () => void;
  togglePause: () => void;
  sendTextMessage: (text: string) => void;
}

// Strips ElevenLabs prosody markers (e.g. "[slow]", "[happy]", "[whisper]") that the
// LLM emits as TTS instructions but the transcript callback delivers verbatim.
// Markers are bracketed lowercase tokens (latin + common Spanish chars + underscore);
// no realistic conversational text uses that bracket pattern, so blanket-stripping is safe.
const PROSODY_MARKER_RE = /\[[a-záéíóúñ_]+\]/gi;
function stripProsodyMarkers(text: string): string {
  return text.replace(PROSODY_MARKER_RE, '').replace(/\s{2,}/g, ' ').trim();
}

function mapInitErrorToI18nKey(status: number | null): string {
  switch (status) {
    case 403: return 'simulationPage.initSessionErrorForbidden';
    case 404: return 'simulationPage.initSessionErrorNotFound';
    case 409: return 'simulationPage.initSessionErrorDocumentsNotReady';
    case 422: return 'simulationPage.initSessionErrorMaestroRequired';
    default: return 'simulationPage.initSessionErrorGeneric';
  }
}

export function useSimulationSession(): UseSimulationSessionReturn {
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  const [status, setStatus] = useState<SessionStatus>('briefing');
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [personaName, setPersonaName] = useState<string | null>(null);
  const [wasInterrupted, setWasInterrupted] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const clearInitError = useCallback(() => setInitError(null), []);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  // Reentrancy guard: blocks duplicate startSession calls (double-click, rapid re-renders)
  // even before setIsStarting state has propagated.
  const startingRef = useRef(false);
  const finishingRef = useRef(false);
  // Lets onDisconnect (defined before confirmFinish) call the latest completion flow
  // when the agent itself ends the call.
  const confirmFinishRef = useRef<(() => Promise<void>) | null>(null);
  const recentlySentTextRef = useRef<Set<string>>(new Set());
  // Snapshot of the mic mute state taken when pause is activated, so resume
  // can restore the user's prior preference instead of always unmuting.
  const wasMutedBeforePauseRef = useRef(false);

  // Persistence refs (fire-and-forget — never block UX)
  const sessionIdRef = useRef<string | null>(null);
  const simulationRef = useRef<SimulationResponse | null>(null);
  const maestroIdRef = useRef<string>('cerrador');
  // Cached init result so resume can reuse conversationToken + dynamic variables
  const lastInitRef = useRef<SessionInitResponse | null>(null);
  const startTimeRef = useRef(0);
  const mockIndexRef = useRef(0);

  // ElevenLabs conversation hook — micMuted as controlled state
  const conversation = useConversation({
    micMuted: isMuted,
    onMessage: ({ message, role }) => {
      if (statusRef.current !== 'in_progress') return;

      // Skip empty payloads (SDK emits interim placeholders for sendUserMessage echoes)
      if (!message || !message.trim()) return;

      // Anti-eco: if the SDK ever echoes a user text turn we already painted+persisted
      // locally via sendTextMessage, skip it. Compare against raw (pre-strip) for fidelity.
      if (role === 'user' && recentlySentTextRef.current.has(message)) return;

      const cleanedText = stripProsodyMarkers(message);
      // Defensive: if the message was nothing but markers, drop it.
      if (!cleanedText) return;

      const msg: ConversationMessage = {
        id: crypto.randomUUID(),
        sender: role === 'user' ? 'user' : 'maestro',
        text: cleanedText,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);

      // Persist transcript (fire-and-forget)
      const sid = sessionIdRef.current;
      if (sid) {
        sessionService.addMessage(sid, {
          role: role === 'user' ? 'User' : 'Assistant',
          content: cleanedText,
          inputType: 'Voice',
        }).catch(() => {});
      }
    },
    onError: (message) => {
      console.error('ElevenLabs error:', message);
    },
    onDisconnect: (details: DisconnectionDetails) => {
      // We initiated the disconnect (confirmFinish, cleanup unmount, etc.) — nothing to surface.
      if (details.reason === 'user') return;
      if (finishingRef.current) return;
      if (statusRef.current !== 'in_progress') return;

      // The agent ended the call itself (natural goodbye once the topics were covered).
      // This is an intended, graceful close — not an interruption: run the normal
      // completion flow so the session is finalized and the report gets generated.
      if (details.reason === 'agent') {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        void confirmFinishRef.current?.();
        return;
      }

      const closeCode = 'closeCode' in details ? details.closeCode : undefined;
      const closeReason = 'closeReason' in details ? details.closeReason : undefined;
      const errorMessage = 'message' in details ? details.message : undefined;

      // Local diagnostic dump for dev (browser console).
      console.error('[ElevenLabs] Unexpected disconnect:', {
        reason: details.reason,
        closeCode,
        closeReason,
        message: errorMessage,
      });

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Persistent backend log — fire-and-forget. Captures payload across navigator reloads
      // and lets ops correlate with NLog file targets / DB rows when reproducing is impossible.
      const sid = sessionIdRef.current;
      if (sid) {
        sessionService.recordEvent(sid, {
          type: 'voice_session_disconnected',
          severity: 'Error',
          payload: {
            reason: details.reason,
            closeCode,
            closeReason,
            message: errorMessage,
            navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : null,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          },
          occurredAt: new Date().toISOString(),
        }).catch(() => { /* swallow — never block UX */ });
      }

      showAlert(t('simulationPage.voiceSessionInterrupted'), 'error');
      setWasInterrupted(true);
      setStatus('finishing');
    },
  });

  // Timer management
  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  useEffect(() => {
    return () => {
      stopTimer();
      if (voiceEnabled && (sessionIdRef.current || lastInitRef.current)) {
        try { conversationRef.current.endSession(); } catch { /* ignore */ }
      }
    };
  }, [stopTimer]);

  const startSession = useCallback(async (simulation: SimulationResponse, maestro: Maestro, isQuickPractice?: boolean) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setIsStarting(true);
    setInitError(null);

    simulationRef.current = simulation;
    maestroIdRef.current = maestro.id;
    startTimeRef.current = Date.now();
    mockIndexRef.current = 0;

    try {
      if (voiceEnabled) {
        // Voice path: single call to backend orchestrator (briefing + ElevenLabs token + session row).
        // QuickPractice has no persisted simulation row — we send the inline params instead
        // of a simulationId so the backend can compose the briefing on the fly.
        let init: SessionInitResponse;
        try {
          init = isQuickPractice
            ? await conversationService.initQuickPractice(
                {
                  simulationType: simulation.simulationType,
                  salePhase: simulation.salePhase || undefined,
                  objections: simulation.objections,
                  sector: simulation.sector,
                  subSector: simulation.subSector,
                  priceRange: simulation.priceRange,
                  difficultyLevel: simulation.difficultyLevel,
                  language: i18n.resolvedLanguage ?? 'es',
                },
                maestro.id,
              )
            : await conversationService.initSession(simulation.id, maestro.id);
        } catch (err) {
          const status = err instanceof SessionInitError ? err.status : null;
          // Init errors (including the 409 DOCUMENTS_NOT_READY race) render inline
          // next to the start button, not as a sticky toast. Salesperson can hit
          // "Retry" manually once conditions change.
          setInitError(t(mapInitErrorToI18nKey(status)));
          return;
        }

        lastInitRef.current = init;
        sessionIdRef.current = init.sessionId;
        setPersonaName(init.dynamicVariables?.persona_name ?? null);

        setStatus('in_progress');
        setIsMuted(false);
        setIsPaused(false);

        try {
          await conversation.startSession({
            conversationToken: init.conversationToken,
            dynamicVariables: init.dynamicVariables,
            overrides: {
              agent: {
                language: init.overrides.agent.language as ConversationConfigOverrideAgentLanguage,
                // System prompt completo construido en backend (SystemPromptFragment +
                // briefing + invariants, todo en idioma de la sesión y con placeholders
                // ya sustituidos). El SDK espera el prompt envuelto en { prompt: string }.
                ...(init.overrides.agent.prompt
                  ? { prompt: { prompt: init.overrides.agent.prompt } }
                  : {}),
                // Greeting del maestro lang-aware. Sin esto el agente arrancaría con el
                // First Message genérico configurado en el dashboard.
                ...(init.overrides.agent.firstMessage
                  ? { firstMessage: init.overrides.agent.firstMessage }
                  : {}),
              },
              ...(init.overrides.tts.voiceId
                ? { tts: { voiceId: init.overrides.tts.voiceId } }
                : {}),
            },
          });
        } catch (err) {
          console.error('ElevenLabs startSession failed:', err);
          showAlert(t('simulationPage.initSessionErrorGeneric'), 'error');
          setStatus('briefing');
          return;
        }

        startTimer();
        return;
      }

      // Text fallback (voiceEnabled === false): local mocks, legacy session creation.
      setStatus('in_progress');
      setIsMuted(false);
      startTimer();

      sessionService.create({
        maestroId: maestro.id,
        simulationId: isQuickPractice ? undefined : simulation.id,
        sessionType: isQuickPractice ? 'QuickPractice' : 'Assigned',
        simulationType: simulation.simulationType,
        difficulty: simulation.difficultyLevel,
      }).then((resp) => {
        sessionIdRef.current = resp.id;
      }).catch(() => {});
    } finally {
      startingRef.current = false;
      setIsStarting(false);
    }
  }, [startTimer, conversation, showAlert, t, i18n.resolvedLanguage]);

  const requestFinish = useCallback(() => {
    if (statusRef.current !== 'in_progress') return;
    setWasInterrupted(false);
    setStatus('finishing');
    stopTimer();
  }, [stopTimer]);

  const confirmFinish = useCallback(async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    setStatus('finishing');

    if (voiceEnabled) {
      try {
        await conversation.endSession();
      } catch (err) {
        console.warn('endSession failed; continuing with completion', err);
      }
    }

    const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const sid = sessionIdRef.current;

    if (sid) {
      try {
        await sessionService.complete(sid, { durationSeconds: duration });
      } catch {
        // POST is fast (no longer waits for the evaluator). Even on failure we move
        // forward; the report view shows Failed state if the report row is missing.
      }
    }
    setStatus('completed');
  }, [conversation]);
  confirmFinishRef.current = confirmFinish;

  const cancelFinish = useCallback(() => {
    setStatus('in_progress');
    startTimer();
  }, [startTimer]);

  const goBackToList = useCallback(() => {
    setStatus('idle');
    setMessages([]);
    setElapsedSeconds(0);
    setIsPaused(false);
    setWasInterrupted(false);
    stopTimer();
    sessionIdRef.current = null;
    simulationRef.current = null;
    lastInitRef.current = null;
    finishingRef.current = false;
  }, [stopTimer]);

  const toggleMute = useCallback(() => {
    // While paused, the mic is forced muted by pause logic — manual toggle is blocked
    // so the silence guarantee for the 95% billing discount is not broken mid-pause.
    if (isPaused) return;
    setIsMuted((prev) => !prev);
  }, [isPaused]);

  const togglePause = useCallback(() => {
    if (!voiceEnabled) return;
    if (statusRef.current !== 'in_progress') return;
    if (conversation.status !== 'connected') return;

    if (!isPaused) {
      wasMutedBeforePauseRef.current = isMuted;
      setIsMuted(true);
      try {
        conversation.sendContextualUpdate(
          'Session paused by user. Remain completely silent. ' +
          'Do not speak, do not ask if the user is there, do not follow up. ' +
          'Wait until you receive a new user message before responding.'
        );
      } catch (err) {
        console.warn('sendContextualUpdate (pause) failed', err);
      }
      setIsPaused(true);
    } else {
      setIsMuted(wasMutedBeforePauseRef.current);
      try {
        conversation.sendContextualUpdate('User has resumed the session.');
      } catch (err) {
        console.warn('sendContextualUpdate (resume) failed', err);
      }
      setIsPaused(false);
    }
  }, [conversation, isMuted, isPaused]);

  // Send a user message via text. Two paths:
  //  - ElevenLabs connected (real IA): forward to conversation.sendUserMessage; the
  //    onMessage callback handles paint + persistence (same path as voice turns).
  //  - Not connected (legacy mock, voiceEnabled=false): paint locally + canned buyer reply.
  const sendTextMessage = useCallback((text: string) => {
    if (statusRef.current !== 'in_progress') return;

    const trimmed = text.trim();
    if (!trimmed) return;

    if (voiceEnabled && conversation.status === 'connected') {
      // ElevenLabs does not echo a non-empty user transcript for sendUserMessage,
      // so we paint and persist the user turn locally; the agent reply lands via onMessage.
      const userMsg: ConversationMessage = {
        id: crypto.randomUUID(),
        sender: 'user',
        text: trimmed,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);

      const sid = sessionIdRef.current;
      if (sid) {
        sessionService.addMessage(sid, {
          role: 'User',
          content: trimmed,
          inputType: 'Text',
        }).catch(() => {});
      }

      recentlySentTextRef.current.add(trimmed);
      setTimeout(() => recentlySentTextRef.current.delete(trimmed), 5000);

      conversation.sendUserMessage(trimmed);
      return;
    }

    // Mock fallback (no ElevenLabs connection)
    const userMsg: ConversationMessage = {
      id: crypto.randomUUID(),
      sender: 'user',
      text: trimmed,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);

    const sid = sessionIdRef.current;
    if (sid) {
      sessionService.addMessage(sid, {
        role: 'User',
        content: trimmed,
        inputType: 'Text',
      }).catch(() => {});
    }

    const responses = MAESTRO_MOCK_RESPONSES[maestroIdRef.current] ?? DEFAULT_MOCK_RESPONSES;
    const responseText = responses[mockIndexRef.current % responses.length];
    mockIndexRef.current += 1;

    setTimeout(() => {
      if (statusRef.current !== 'in_progress') return;

      const maestroMsg: ConversationMessage = {
        id: crypto.randomUUID(),
        sender: 'maestro',
        text: responseText,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, maestroMsg]);

      const currentSid = sessionIdRef.current;
      if (currentSid) {
        sessionService.addMessage(currentSid, {
          role: 'Assistant',
          content: responseText,
          inputType: 'Text',
        }).catch(() => {});
      }
    }, 1500);
  }, [conversation]);

  return {
    status,
    messages,
    elapsedSeconds,
    sessionId: sessionIdRef.current,
    personaName,
    connectionStatus: voiceEnabled ? conversation.status : 'connected',
    isSpeaking: voiceEnabled ? conversation.isSpeaking : false,
    isMuted,
    isPaused,
    isStarting,
    wasInterrupted,
    initError,
    clearInitError,
    voiceEnabled,
    startSession,
    requestFinish,
    confirmFinish,
    cancelFinish,
    goBackToList,
    toggleMute,
    togglePause,
    sendTextMessage,
  };
}
